/**
 * Personnalisation native La Pelucherie — composant par briques composables.
 * Rendu par snippets/perso-options.liquid + perso-option-card.liquid.
 * Source unique de vérité du total : émet `perso:change` (écouté par le total inline et la barre sticky).
 * Le hook d'ajout panier (items[]) vit dans product-form.js.
 */
/**
 * Broderie : les emojis ne sont pas brodables (ni la machine ni la police ne les rendent).
 * On les retire à la saisie plutôt que de bloquer l'ajout panier — moins frustrant,
 * et le filet dans cardValues() garantit qu'aucun n'atteint le panier (collage, autofill).
 */
var PERSO_EMOJI_RE = (function () {
  try {
    // Pictogrammes + sélecteurs de variante + ZWJ + keycaps + drapeaux régionaux.
    return new RegExp(
      '(?:\\p{Extended_Pictographic}|\\p{Regional_Indicator})(?:\\uFE0F|\\uFE0E|\\u200D|\\p{Extended_Pictographic}|\\p{Regional_Indicator})*|[#*0-9]\\uFE0F?\\u20E3',
      'gu'
    );
  } catch (e) {
    // Fallback navigateurs sans property escapes : plages surrogate des blocs emoji.
    return /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u21FF\u2300-\u27BF\u2B00-\u2BFF\uFE0F\u200D]/g;
  }
})();

function persoStripEmoji(value) {
  return (value || '')
    .replace(PERSO_EMOJI_RE, '')
    .replace(/[ \t]{2,}/g, ' ');
}

if (!customElements.get('perso-options')) {
  customElements.define(
    'perso-options',
    class PersoOptions extends HTMLElement {
      connectedCallback() {
        // Avant tout update() : un composant du panier ne diffuse pas perso:change.
        this.inCart = this.dataset.scope === 'cart';
        this.checkboxes = Array.from(this.querySelectorAll('[data-perso-checkbox]'));
        this.totalEl = this.querySelector('[data-perso-total]');
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}€';
        this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;

        this.checkboxes.forEach((cb) => {
          cb.addEventListener('change', () => this.onToggle(cb));
          this.onToggle(cb); // état initial cohérent (champs masqués/désactivés)
        });

        this.wireLightboxes();
        this.wireEmojiFilter();
        this.setupEligibility();
        // Après wireEmojiFilter : le compteur doit lire la valeur déjà nettoyée.
        this.wireFields();

        // Suit le changement de variante : le prix de base ET l'éligibilité des options
        // changent selon la taille. Thème V1 (Fluorescent) : événement DOM sur document
        // `product:variant-change` avec { detail: { variant } } (cf. theme.js dispatchCustomEvent).
        // Pas dans le panier : la variante d'une ligne est figée, et l'événement
        // viendrait de la fiche produit ouverte derrière le tiroir.
        if (!this.inCart) {
          this._onVariantChange = (event) => {
            const variant = event && event.detail && event.detail.variant;
            if (!variant) return;
            if (typeof variant.price === 'number') this.basePrice = variant.price;
            this.applyEligibility(variant.options);
            this.update();
          };
          document.addEventListener('product:variant-change', this._onVariantChange);
        }

        this.update();
      }

      // Lit les tailles éligibles (liste blanche) par carte, puis applique l'état initial
      // d'après la variante rendue côté serveur (data-current-options).
      setupEligibility() {
        this.cards = Array.from(this.querySelectorAll('[data-perso-option]'));
        this.cards.forEach((card) => {
          const raw = card.dataset.eligibleSizes || '';
          card._eligibleSizes = raw ? raw.split('|').map(PersoOptions.norm).filter(Boolean) : [];
        });
        this.applyEligibility((this.dataset.currentOptions || '').split('|'));
      }

      // Masque (et décoche) les options indisponibles pour les valeurs d'option de la variante courante.
      // Liste vide = option dispo partout (comportement par défaut).
      applyEligibility(optionValues) {
        if (!this.cards) return;
        const vals = (optionValues || []).map(PersoOptions.norm).filter(Boolean);
        this.cards.forEach((card) => {
          const elig = card._eligibleSizes || [];
          const ok = elig.length === 0 || elig.some((e) => vals.includes(e));
          card.hidden = !ok;
          if (!ok) {
            const cb = card.querySelector('[data-perso-checkbox]');
            if (cb && cb.checked) {
              cb.checked = false;
              this.onToggle(cb); // vide les champs + recalcule → pas de compagnon non éligible au panier
            }
          }
        });
      }

      // Aperçu agrandi d'une option (vignette ou « En savoir + ») → ouvre la lightbox de la carte.
      wireLightboxes() {
        this.querySelectorAll('[data-perso-zoom]').forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            const card = trigger.closest('[data-perso-option]');
            const dlg = card && card.querySelector('[data-perso-lightbox]');
            if (dlg && typeof dlg.showModal === 'function') {
              dlg.showModal();
            }
          });
        });
        this.querySelectorAll('[data-perso-lightbox]').forEach((dlg) => {
          // Clic sur le fond (hors contenu) ou bouton fermer → ferme.
          dlg.addEventListener('click', (e) => {
            if (e.target === dlg || e.target.closest('[data-perso-close]')) {
              dlg.close();
            }
          });
        });
      }

      // Retire les emojis à la saisie (frappe et collage) : ils ne sont pas brodables.
      // Le curseur est repositionné sur la partie conservée pour ne pas sauter en fin de champ.
      wireEmojiFilter() {
        this.querySelectorAll('input[data-perso-input], textarea[data-perso-input]').forEach((input) => {
          input.addEventListener('input', () => {
            const raw = input.value;
            const clean = persoStripEmoji(raw);
            if (clean === raw) return;
            const caret = typeof input.selectionStart === 'number' ? input.selectionStart : raw.length;
            const before = persoStripEmoji(raw.slice(0, caret)).length;
            input.value = clean;
            try {
              input.setSelectionRange(before, before);
            } catch (e) {
              /* type de champ sans sélection : sans conséquence */
            }
            this.showEmojiHint(input);
          });
        });
      }

      // Message discret sous le champ, créé à la volée puis effacé.
      showEmojiHint(input) {
        const field = input.closest('.perso-field');
        if (!field) return;
        let hint = field.querySelector('[data-perso-hint]');
        if (!hint) {
          hint = document.createElement('p');
          hint.className = 'perso-field__hint';
          hint.setAttribute('data-perso-hint', '');
          hint.setAttribute('role', 'status');
          hint.textContent = 'Les emojis ne peuvent malheureusement pas être brodés.';
          field.appendChild(hint);
        }
        hint.classList.add('perso-field__hint--visible');
        clearTimeout(hint._timer);
        hint._timer = setTimeout(() => hint.classList.remove('perso-field__hint--visible'), 5000);
      }

      // Compteur de caractères + effacement de l'erreur dès que le client corrige.
      wireFields() {
        this.querySelectorAll('[data-perso-input]').forEach((input) => {
          this.syncCount(input);
          input.addEventListener('input', () => {
            this.syncCount(input);
            if (!input.value.trim()) return;
            this.clearFieldError(input);
            // Recompte : l'alerte doit suivre la correction, pas rester sur le
            // nombre affiché au moment du clic.
            const left = this.querySelectorAll('.perso-field__input--error').length;
            if (left) this.showError(this.incompleteMessage(left));
            else this.clearError();
          });
        });
      }

      syncCount(input) {
        const field = input.closest('.perso-field');
        const counter = field && field.querySelector('[data-perso-count]');
        if (!counter) return;
        const max = input.getAttribute('maxlength');
        // Champ vide : on annonce la limite en clair (« 20 caractères max »).
        // Dès que le client tape, le compteur prend le relais.
        if (!input.value.length && counter.dataset.countEmpty) {
          counter.textContent = counter.dataset.countEmpty;
          counter.classList.remove('perso-field__count--full');
          return;
        }
        counter.textContent = input.value.length + '/' + max;
        counter.classList.toggle('perso-field__count--full', !!max && input.value.length >= parseInt(max, 10));
      }

      markFieldError(input) {
        input.classList.add('perso-field__input--error');
        input.setAttribute('aria-invalid', 'true');
        const field = input.closest('.perso-field');
        const msg = field && field.querySelector('[data-perso-field-error]');
        if (msg) msg.hidden = false;
      }

      clearFieldError(input) {
        input.classList.remove('perso-field__input--error');
        input.removeAttribute('aria-invalid');
        const field = input.closest('.perso-field');
        const msg = field && field.querySelector('[data-perso-field-error]');
        if (msg) msg.hidden = true;
      }

      // Alerte globale du bloc (role=alert → annoncée par les lecteurs d'écran).
      showError(text) {
        const el = this.querySelector('[data-perso-error]');
        if (!el) return;
        el.textContent = text;
        el.hidden = false;
      }

      clearError() {
        const el = this.querySelector('[data-perso-error]');
        if (!el) return;
        el.hidden = true;
        el.textContent = '';
      }

      // Message d'erreur global, depuis les locales (cf. data-msg-* rendus par le snippet).
      incompleteMessage(missing) {
        if (missing > 1) {
          const tpl = this.dataset.msgIncompleteMany || '__N__';
          return tpl.replace('__N__', missing);
        }
        return this.dataset.msgIncompleteOne || '';
      }

      onToggle(cb) {
        const card = cb.closest('[data-perso-option]');
        if (!card) return;
        const fields = card.querySelector('[data-perso-fields]');
        const inputs = card.querySelectorAll('[data-perso-input]');
        if (fields) fields.hidden = !cb.checked;
        inputs.forEach((i) => {
          i.disabled = !cb.checked;
          if (!cb.checked) i.value = '';
          this.clearFieldError(i);
          this.syncCount(i);
        });
        // Décocher une option retire ses champs manquants → l'alerte n'a plus lieu d'être.
        if (!cb.checked) this.clearError();
        this.update();
      }

      // Recalcule le total et diffuse l'état (perso:change).
      update() {
        let addon = 0;
        let count = 0;
        const summary = [];
        this.checkboxes.forEach((cb) => {
          if (!cb.checked) return;
          count += 1;
          addon += parseInt(cb.dataset.companionPrice, 10) || 0;
          if (cb.dataset.optionTitle) summary.push(cb.dataset.optionTitle);
        });
        const total = this.basePrice + addon;

        if (this.totalEl) {
          if (count > 0) {
            this.totalEl.hidden = false;
            this.totalEl.textContent = 'Total avec personnalisation : ' + this.formatMoney(total);
          } else {
            this.totalEl.hidden = true;
            this.totalEl.textContent = '';
          }
        }

        // Le total de la fiche et la barre fixe écoutent cet événement : un
        // composant du panier ne doit pas leur faire afficher un supplément.
        if (this.inCart) return;
        document.dispatchEvent(
          new CustomEvent('perso:change', {
            detail: { addonCents: addon, totalCents: total, count, totalText: this.formatMoney(total), summary },
          })
        );
      }

      hasSelection() {
        return this.checkboxes.some((cb) => cb.checked);
      }

      // Valide les champs requis des options actives.
      validate() {
        let firstInvalid = null;
        let missing = 0;
        this.checkboxes.forEach((cb) => {
          if (!cb.checked) return;
          const card = cb.closest('[data-perso-option]');
          card.querySelectorAll('[data-perso-input][data-required]').forEach((input) => {
            if (!input.value.trim()) {
              missing += 1;
              this.markFieldError(input);
              if (!firstInvalid) firstInvalid = input;
            } else {
              this.clearFieldError(input);
            }
          });
        });
        const message = firstInvalid ? this.incompleteMessage(missing) : null;
        if (message) this.showError(message);
        else this.clearError();
        return { ok: !firstInvalid, firstInvalid, missing, message };
      }

      // Valeurs des champs d'une carte (label → valeur).
      cardValues(card) {
        const props = {};
        card.querySelectorAll('[data-perso-input]').forEach((input) => {
          const label = this.fieldLabel(input);
          // Filet : garantit qu'aucun emoji n'atteint le panier même si l'écoute input a été contournée.
          // Même logique pour la longueur — maxlength borne la frappe et le collage,
          // pas une valeur injectée (autofill, extension, script).
          const max = parseInt(input.getAttribute('maxlength'), 10);
          const cleaned = persoStripEmoji(input.value);
          const value = (max > 0 ? cleaned.slice(0, max) : cleaned).trim();
          if (label && value) props[label] = value;
        });
        return props;
      }

      // Handles des options cochées, dans l'ordre d'affichage. Identifiants stables
      // (le titre ne l'est pas : plusieurs options partagent « Broderie sur la patte »).
      getSelectedHandles() {
        return this.checkboxes
          .filter((cb) => cb.checked)
          .map((cb) => cb.dataset.optionHandle || '')
          .filter(Boolean);
      }

      // Properties pour la ligne peluche : uniquement les options SANS compagnon (incluses/0 €),
      // plus `_perso_options` = TOUTES les options choisies (compagnon ou non) → le taux
      // d'attach se lit sur la seule ligne peluche, sans recoller le groupe.
      getMainProperties() {
        const props = {};
        const handles = this.getSelectedHandles();
        if (handles.length) props._perso_options = handles.join(',');
        this.checkboxes.forEach((cb) => {
          if (!cb.checked || cb.dataset.companionVariant) return;
          const card = cb.closest('[data-perso-option]');
          const vals = this.cardValues(card);
          const prefix = cb.dataset.optionTitle ? cb.dataset.optionTitle + ' — ' : '';
          Object.keys(vals).forEach((k) => {
            props[prefix + k] = vals[k];
          });
        });
        return props;
      }

      // Lignes compagnons (portent le prix). Le texte brodé voyage sur SA ligne → pas de collision.
      getCompanionItems(groupId) {
        const items = [];
        this.checkboxes.forEach((cb) => {
          if (!cb.checked || !cb.dataset.companionVariant) return;
          const card = cb.closest('[data-perso-option]');
          items.push({
            id: cb.dataset.companionVariant,
            quantity: 1,
            properties: Object.assign({}, this.cardValues(card), {
              _perso_group: groupId,
              _perso_option: cb.dataset.optionHandle || '',
            }),
          });
        });
        return items;
      }

      fieldLabel(input) {
        const name = input.getAttribute('name') || '';
        const m = name.match(/^properties\[(.+)\]$/);
        return m ? m[1] : null;
      }

      formatMoney(cents) {
        // Décimales masquées quand elles valent zéro (cf. money_without_trailing_zeros).
        const amount = (cents / 100).toFixed(2).replace(/[.,]00$/, '');
        const fmt = this.moneyFormat;
        let out;
        if (fmt.indexOf('{{amount}}') !== -1) {
          out = fmt.replace('{{amount}}', amount);
        } else if (fmt.indexOf('{{amount_no_decimals}}') !== -1) {
          out = fmt.replace('{{amount_no_decimals}}', Math.round(cents / 100));
        } else if (fmt.indexOf('{{amount_with_comma_separator}}') !== -1) {
          out = fmt.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
        } else if (fmt.indexOf('{{amount_no_decimals_with_comma_separator}}') !== -1) {
          out = fmt.replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
        } else {
          out = amount + '€';
        }
        // Le money_format de la boutique contient du HTML (<span class="money">…</span>) → on le retire.
        return out.replace(/<[^>]+>/g, '').trim();
      }

      static norm(s) {
        return (s || '').toString().trim().toLowerCase();
      }

      disconnectedCallback() {
        if (this._onVariantChange) document.removeEventListener('product:variant-change', this._onVariantChange);
      }
    }
  );
}
